import React, { createContext, lazy, Suspense, useCallback, useContext, useMemo, useState } from 'react';

const PanelExportsDrawer = lazy(() => import('components/PanelExportsDrawer'));
const StudyActivityDrawer = lazy(() => import('components/Study/StudyActivityDrawer'));

/**
 * Registry of drawers the provider can open by name.
 *
 * To add a new drawer:
 *   1. Build a component that accepts `{ isOpen, onClose, ...required props }`.
 *   2. Add an entry here keyed by the name callers will pass to `open()`.
 *   3. Open it from anywhere with `useDrawer().open('myDrawer', { ... })`.
 *
 * @type {Record<DrawerName, React.ComponentType<any>>}
 */
const DRAWERS = {
    panelExports: PanelExportsDrawer,
    studyActivity: StudyActivityDrawer
};

/** @typedef {'panelExports' | 'studyActivity'} DrawerName */

/**
 * @typedef {Object} DrawerContextValue
 * @property {(name: DrawerName, props?: Record<string, any>) => void} open
 *     Open a registered drawer by name. Unknown names log a warning and no-op.
 * @property {(name: DrawerName) => void} close
 *     Close a single drawer. No-op if not currently open.
 * @property {(name: DrawerName) => boolean} isOpen
 *     Whether the named drawer is currently mounted/visible.
 */

/** @type {React.Context<DrawerContextValue|null>} */
const DrawerContext = createContext(null);

/**
 * Generic drawer registry. Mount once near the App root so any descendant can
 * open a registered drawer by name without prop drilling.
 *
 * Drawers stay decoupled from this provider — they receive only `isOpen`,
 * `onClose`, and whatever data the caller passes via `open(name, props)`.
 * Pusher / data-fetching concerns belong in their own providers (e.g.
 * `ExportNotificationsProvider`); this context's responsibility stays narrow.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @returns {JSX.Element}
 */
export function DrawerProvider({ children }) {
    const [openDrawers, setOpenDrawers] = useState({});

    const open = useCallback((name, props = {}) => {
        if (!DRAWERS[name]) {
            // eslint-disable-next-line no-console
            console.warn(`DrawerProvider: unknown drawer "${name}"`);
            return;
        }
        setOpenDrawers(state => ({ ...state, [name]: props }));
    }, []);

    const close = useCallback(name => {
        setOpenDrawers(state => {
            if (!(name in state)) return state;
            const next = { ...state };
            delete next[name];
            return next;
        });
    }, []);

    const isOpen = useCallback(name => name in openDrawers, [openDrawers]);

    const value = useMemo(() => ({ open, close, isOpen }), [open, close, isOpen]);

    return (
        <DrawerContext.Provider value={value}>
            {children}
            <Suspense fallback={null}>
                {Object.entries(openDrawers).map(([name, drawerProps]) => {
                    const Drawer = DRAWERS[name];
                    return <Drawer key={name} isOpen onClose={() => close(name)} {...drawerProps} />;
                })}
            </Suspense>
        </DrawerContext.Provider>
    );
}

/**
 * Access the drawer registry from a function component.
 *
 * @returns {DrawerContextValue}
 * @throws {Error} When called outside `DrawerProvider`.
 */
export function useDrawer() {
    const ctx = useContext(DrawerContext);
    if (!ctx) {
        throw new Error('useDrawer must be used within DrawerProvider');
    }
    return ctx;
}

/**
 * HOC for class components that need imperative access to drawers.
 * Usage: `export default withDrawer(MyClassComponent)` then call
 * `this.props.drawer.open('panelExports')`.
 *
 * @template P
 * @param {React.ComponentType<P & { drawer: DrawerContextValue }>} Component
 *     The class/function component receiving an injected `drawer` prop.
 * @returns {React.FC<P>} Wrapped component that consumes `useDrawer()` and
 *     forwards it as `drawer`.
 */
export const withDrawer = Component => props => {
    const drawer = useDrawer();
    return <Component {...props} drawer={drawer} />;
};
